// The shared frame of the full-screen ADD / EDIT forms (KAN-1070; customers
// first, team member / new company / edit plan next). It composes the kit and
// nothing else: FullScreenFlowShell (portal + enter/exit), FullScreenFlowHeader,
// FullScreenTwoPane + FullScreenSummaryRail (desktop), FullScreenBottomBar
// (phone / tablet) and Log Job's phone pieces (section jump strip, scroll wrap).
//
//   phone    header (tile + "Area" eyebrow + title + ✕) · jump strip · grey body
//            of section cards · white bottom bar
//   tablet   the same, the cards held to the 680 column
//   desktop  header · cards on the left (max 880, centred) · 360 summary rail
//            (identity, `rail` cards, then the footer as the rail's submit block)
//
// The caller owns the cards (each wrapped in `FormSection` so the jump strip
// finds it), the rail contents and the `footer` node (problem line +
// primary). The bottom bar reserves its MEASURED height, so the last card is
// never hidden behind it (KAN-1069).
import { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import FullScreenFlowShell from './FullScreenFlowShell';
import FullScreenFlowHeader from './FullScreenFlowHeader';
import FullScreenTwoPane from './FullScreenTwoPane';
import FullScreenSummaryRail from './FullScreenSummaryRail';
import FullScreenBottomBar from './FullScreenBottomBar';
import FullScreenSectionJumpStrip from './FullScreenSectionJumpStrip';
import { useSectionJump } from '../../../hooks/logJob/useSectionJump';
import useBarHeightVar from '../../../hooks/ui/useBarHeightVar';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import { styles } from '../../../pages/logJobStyles';
import { layoutStyles as L } from '../../../pages/logJobLayoutStyles';
import { overlayLayers } from '../../../ui/overlayLayers';
import { foundation } from '../../../ui';

/** The attribute a section carries so the phone jump strip can find it. */
export const FORM_SECTION_ATTR = 'data-form-section';
const BAR_HEIGHT_VAR = '--cvx-form-bar-h';
const BAR_FALLBACK = 'calc(76px + max(12px, env(safe-area-inset-bottom, 0px)))';

const cardStack = { display: 'flex', flexDirection: 'column', gap: foundation.spacePx[3], minWidth: 0 };
// Fixed to the visual viewport like Log Job's bar, so a keyboard never exposes the page under it.
const fixedBar = { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 100 };

/** Wrap one section card so the jump strip can scroll to it. */
export function FormSection({ sectionKey, children }) {
  return <div {...{ [FORM_SECTION_ATTR]: sectionKey }} style={{ minWidth: 0 }}>{children}</div>;
}

function CompactBody({ layout, sections, children, footer, testId }) {
  const reduced = useReducedMotion();
  const phone = layout === 'phone';
  const rootRef = useRef(null);
  const barRef = useRef(null);
  // Callback-ref state (not useRef): a remounted scroller must re-observe (same reason as Log Job).
  const [scrollEl, setScrollEl] = useState(null);
  const keys = useMemo(() => (sections || []).map((s) => s.key), [sections]);
  const { activeKey, jumpTo } = useSectionJump({ scrollEl, keys, enabled: layout !== 'desktop', reduced, attr: FORM_SECTION_ATTR });
  useBarHeightVar(barRef, rootRef, BAR_HEIGHT_VAR);
  return (
    <div ref={rootRef} style={{ ...styles.container, animation: 'none', backgroundColor: 'transparent', flex: 1, minHeight: 0 }} data-testid={`${testId}-compact`}>
      {sections && sections.length > 1 ? (
        <FullScreenSectionJumpStrip sections={sections} activeKey={activeKey} onJump={jumpTo} />
      ) : null}
      <div
        ref={setScrollEl}
        data-cvx-bar-reserve
        style={{ ...styles.scrollWrap, paddingBottom: `var(${BAR_HEIGHT_VAR}, ${BAR_FALLBACK})` }}
        data-testid={`${testId}-scroll`}
      >
        <div
          style={phone
            ? { ...styles.content, ...styles.contentMobile, ...cardStack }
            : { ...L.column, ...L.columnPadTablet, ...cardStack }}
        >
          {children}
        </div>
      </div>
      <div ref={barRef} style={fixedBar} data-testid={`${testId}-bar`}>
        <FullScreenBottomBar layout={layout}>{footer}</FullScreenBottomBar>
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen - false plays the exit beat.
 * @param {'phone'|'tablet'|'desktop'} props.layout - latched by the caller at open.
 * @param {import('react').ReactNode} props.icon - canon icon element for the header tile.
 * @param {string} [props.areaLabel] - "Customers" (phone eyebrow / desktop crumb); none = title alone.
 * @param {string} props.title
 * @param {string} [props.subtitle] - a quiet line with the title (under it on touch, inline on desktop).
 * @param {() => void} props.onClose - the guarded close.
 * @param {() => void} [props.onExitComplete] - fired once after the exit beat (see FullScreenFlowShell).
 * @param {boolean} [props.closeDisabled]
 * @param {{key: string, label: string}[]} [props.sections] - the phone jump strip.
 * @param {import('react').ReactNode} props.children - the section cards (each in a `FormSection`).
 * @param {import('react').ReactNode} [props.railIdentity] - desktop rail header.
 * @param {import('react').ReactNode} [props.rail] - desktop rail cards.
 * @param {import('react').ReactNode} props.footer - problem line + primary. Bottom bar on touch, the rail's submit block on desktop.
 * @param {object} [props.moreMenu] - header ⋯ menu ({ items, ariaLabel, testId, testIdPrefix }); isMobile is set from the layout.
 * @param {number} [props.zIndex]
 * @param {string} [props.testId]
 */
export default function FullScreenFormFrame({
  isOpen, layout, icon, areaLabel, title, subtitle, onClose, onExitComplete, closeDisabled = false, sections, children,
  railIdentity = null, rail = null, footer, moreMenu, zIndex = overlayLayers.formDrawerPanel, testId = 'fullscreen-form',
}) {
  const intl = useIntl();
  const desktop = layout === 'desktop';
  const railNode = desktop ? (
    <FullScreenSummaryRail identity={railIdentity} submit={footer} testId={`${testId}-rail`}>
      {rail}
    </FullScreenSummaryRail>
  ) : null;
  return (
    <FullScreenFlowShell
      isOpen={isOpen}
      onExitComplete={onExitComplete}
      layout={layout}
      zIndex={zIndex}
      dialogProps={{ 'aria-label': title, 'data-testid': testId }}
    >
      <FullScreenFlowHeader
        layout={layout}
        icon={icon}
        title={title}
        areaLabel={areaLabel}
        subtitle={subtitle}
        eyebrow={layout === 'phone' ? areaLabel : undefined}
        onClose={onClose}
        closeDisabled={closeDisabled}
        moreMenu={moreMenu ? { isMobile: layout === 'phone', ...moreMenu } : undefined}
        closeAriaLabel={intl.formatMessage({ id: 'common.close', defaultMessage: 'Close' })}
        testId={`${testId}-header`}
      />
      {desktop ? (
        <FullScreenTwoPane form={<div style={cardStack}>{children}</div>} rail={railNode} testId={`${testId}-two-pane`} />
      ) : (
        <CompactBody layout={layout} sections={sections} footer={footer} testId={testId}>{children}</CompactBody>
      )}
    </FullScreenFlowShell>
  );
}
